/**
 * №16 S5 — the PROGRAM (journey) view (own route, `place/:entity/:id/program`).
 * The forward-chain (W20) rendered as ONE timeline: each origin-linked instance is
 * a hop with its own spine; a discreet "handed off →" divider carries the journey
 * label between hops; the live frontier is flagged. A Program is a READ MODEL — this
 * view never mutates; execution stays on each hop's own process route (D-WF14).
 *
 * Built entirely from the validated full-view grammar (WorkflowSpine + the card /
 * statuschip / wf-seg vocabulary) — no new visual element, token-only styles.
 */
import { Link, useParams } from "react-router";
import { useAnchorWorkflows, useProgramJourney, useAgentDirectory, type ProgramInstanceView, type DetailSegment, type DetailStep } from "../../hooks/use-workflow";
import { WorkflowSpine } from "../../components/workflow/WorkflowSpine";
import { assigneeDisplay } from "../../components/workflow/wf-assignee";
import { PencilGlyph, ClockGlyph, ForkGlyph } from "../../components/workflow/wf-glyphs";

export function ProgramView() {
  const { entity, id } = useParams<{ entity: string; id: string }>();
  const anchorType = entity ?? "matter";
  const anchor = useAnchorWorkflows(id, anchorType);
  // Seed from the newest instance on this anchor; program() walks the whole chain.
  const seed = anchor.data?.instances.find((i) => i.status === "active") ?? anchor.data?.instances[0];
  const prog = useProgramJourney(seed?.instanceNo);
  const p = prog.data;

  return (
    <div className="screen">
      {anchor.isLoading || prog.isLoading ? (
        <div className="card"><div className="skeleton"><div className="sk-line" style={{ width: "45%" }} /><div className="sk-line" style={{ width: "80%" }} /><div className="sk-line" style={{ width: "60%" }} /></div></div>
      ) : !p || !p.found || p.instances.length === 0 ? (
        <div className="card"><div className="empty-state"><h3>No journey on this record</h3><p>This process is not part of a chain yet. A journey forms when a terminal outcome hands off to a successor.</p></div></div>
      ) : (
        <>
          <div className="matterhead">
            <span className="id mono">Journey</span>
            <h1>{p.journeyLabel}</h1>
            <span className="statuschip"><span className="dot" style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />{p.instanceCount} stage{p.instanceCount === 1 ? "" : "s"}</span>
            {p.crossesAnchors ? <span className="responsible">crosses {p.anchors.length} records</span> : null}
          </div>

          {p.carriedContext.length > 0 ? (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="card__h" style={{ cursor: "default" }}>Carried through the journey <span className="sub">named values passed from one stage to the next</span></div>
              <div className="card__b"><div className="chiprow" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {p.carriedContext.map((c, i) => (
                  <span className="badge badge--soft" key={`${c.intoInstanceNo}-${c.name}-${i}`}>{c.name}: {c.value || "—"}</span>
                ))}
              </div></div>
            </div>
          ) : null}

          {p.instances.map((hop, i) => (
            <div key={hop.instanceNo}>
              {i > 0 ? (
                <div className="wf-handoff" style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 0 14px 14px", color: "var(--text-2)", fontSize: 13 }}>
                  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path d="M12 5v14M12 19l6-6M12 19l-6-6" /></svg>
                  <span>handed off{p.instances[i - 1]?.endingLabel ? ` — ${p.instances[i - 1]!.endingLabel}` : ""}</span>
                </div>
              ) : null}
              <HopBlock hop={hop} index={i} anchorType={anchorType} crossesAnchors={p.crossesAnchors} />
            </div>
          ))}

          <p className="makernote" style={{ marginTop: 8 }}>One journey, many stages. Each stage is its own process — the parent ended, the child began, and there is no way back. Open a stage to run it.</p>
        </>
      )}
    </div>
  );
}

function HopBlock({ hop, index, anchorType, crossesAnchors }: { hop: ProgramInstanceView; index: number; anchorType: string; crossesAnchors: boolean }) {
  const agentNames = useAgentDirectory().data;
  const processTo = `/app/place/${hop.subjectType || anchorType}/${hop.subjectNo}/process`;
  // The live hop leads; finished / not-yet hops recede so the eye lands on what is
  // happening NOW. On the frontier we surface the current step(s) + who owns them —
  // the same "you are here" the matter panel shows, so the journey orients you.
  const now = hop.isFrontier ? currentWork(hop.segments) : null;
  const hasNow = !!now && (now.steps.length > 0 || now.decisions.length > 0);
  return (
    <div
      className={`card${hop.isFrontier ? " card--accent" : ""}`}
      style={hop.isFrontier ? undefined : { opacity: 0.5 }}
    >
      <div className="card__h" style={{ cursor: "default" }}>
        <span className="wf-seg__dot" style={hop.done ? { background: "var(--accent)", color: "var(--accent-fg)", borderColor: "transparent" } : hop.isFrontier ? { background: "var(--accent)", color: "var(--accent-fg)", borderColor: "transparent" } : { borderStyle: "dashed", background: "none" }} />
        {index + 1}. {prettyCode(hop.templateCode)}
        <span className="sub">
          {hop.done ? "finished" : hop.isFrontier ? "in progress — you are here" : hop.status}
          {crossesAnchors ? ` · ${hop.subjectType} ${hop.subjectNo}` : ""}
        </span>
        <Link to={processTo} className="listrow__go" style={{ marginLeft: "auto", fontSize: 13 }}>Open this process →</Link>
      </div>
      <div className="card__b">
        <WorkflowSpine segments={hop.segments} />
        {hasNow ? (
          <div className="checklist" style={{ marginTop: 14 }}>
            <div className="sub" style={{ marginBottom: 8, fontWeight: 600, color: "var(--accent)" }}>Now{now!.segName ? ` · ${now!.segName}` : ""}</div>
            {now!.decisions.map((d) => (
              <div className="check-item" key={`dec-${d}`}>
                <span className="mark mark--now"><ForkGlyph /></span>
                <div><div className="label">Decide: {d}</div><div className="sub">a choice is waiting</div></div>
              </div>
            ))}
            {now!.steps.map((s) => {
              const waiting = s.status === "waiting";
              const asg = assigneeDisplay(s.assigneeKind, s.assigneeCode, agentNames);
              return (
                <div className="check-item" key={s.stepEntryNo}>
                  <span className={`mark ${!waiting && asg.isAgent ? "mark--now" : ""}`}>
                    {waiting ? <ClockGlyph /> : asg.isAgent ? <PencilGlyph /> : null}
                  </span>
                  <div><div className="label">{s.name}</div><div className="sub">{waiting ? "waiting" : asg.isAgent ? `${asg.who} is on this` : asg.who}</div></div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The current work on the live frontier — open segment(s), their ready/waiting
 *  steps and any ripe decision — so a reader can orient without opening the process. */
function currentWork(segments: DetailSegment[]): { segName: string; steps: DetailStep[]; decisions: string[] } {
  const open = segments.filter((s) => s.state === "open");   // №16.2.1-C — the ONE state field
  const steps: DetailStep[] = [];
  const decisions: string[] = [];
  for (const s of open) {
    for (const st of s.steps) if (st.status === "ready" || st.status === "waiting") steps.push(st);
    if (s.decision && s.decision.ripe && !s.decision.resolved) decisions.push(s.decision.name);
  }
  return { segName: open.map((s) => s.name).join(" · "), steps, decisions };
}

function prettyCode(code: string): string {
  return code.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
